// Les offres de lancement (D-186 ; précise D-142 et D-159) : ce qui ne dépend que des données, pour la console de l'éditeur.
// Une offre : un titre, une phrase, un avantage (saison offerte, mois offerts, frais offerts, remise), un début, une fin, des
// places. Une seule en ligne à la fois ; sur arceau.app seulement pendant ses dates et tant qu'il reste des places ; « Encore
// N clubs » seulement sous 20 places, sauf si la console l'affiche toujours. La base décide (migration
// 20261109000000_offres_lancement.sql) ; ici, les libellés et l'état. Logique pure, testée (offres.test.ts).

export type Perk = 'season' | 'months' | 'fees' | 'discount';

export interface OfferClub {
  club_id: string; name: string; short_name: string | null; logo_url: string | null; primary_color: string | null; secondary_color: string | null;
  entered_at: string; licencies: number | null; source: 'signup' | 'request'; plan: string; plan_until: string | null;
}
export interface LaunchOffer {
  id: string; title: string; body: string; perk: Perk;
  perk_until: string | null; perk_months: number | null; perk_percent: number | null; site_included: boolean;
  starts_on: string | null; ends_on: string | null; quota: number; taken: number; show_left: boolean;
  active: boolean; activated_at: string | null; cut_at: string | null; created_at: string; updated_at: string;
  /** Clubs entrés (créés pendant l'offre), demandes faites pendant l'offre pas encore créées, les 12 derniers entrés. */
  entered: number; pending: number; clubs: OfferClub[];
  /** Les licenciés déclarés de chaque club entré (null : pas dit), pour les abonnements à venir. */
  licencies: (number | null)[];
}
/** Les abonnements à venir des clubs entrés, au prix D-142 (TTC par an) ; un club sans licenciés déclarés ne compte pas. */
export const comingRevenue = (o: Pick<LaunchOffer, 'licencies'>): number => o.licencies.reduce<number>((s, n) => s + (n ? d142Price(n) : 0), 0);
/** Ce qu'on enregistre (console_launch_offer_save). */
export type OfferDraft = Pick<LaunchOffer, 'title' | 'body' | 'perk' | 'perk_until' | 'perk_months' | 'perk_percent' | 'site_included' | 'starts_on' | 'ends_on' | 'quota' | 'taken' | 'show_left'> & { id?: string };

/** Sous ce nombre de places, « Encore N clubs » s'affiche toujours (D-186 : « encore 47 clubs » ne pousse personne). */
export const SHOW_LEFT_UNDER = 20;
/** La mise en route (D-142), offerte par l'avantage « Frais offerts ». */
export const SETUP_FEE_TTC = 99;
/** La grille D-142 affichée sur la page de vente (prix TTC par saison) : [plafond de licenciés, prix]. La facturation de l'app
 *  suit encore D-078 (src/domain/console.ts) ; ceci ne sert qu'à estimer les abonnements à venir des clubs entrés. */
export const D142_TIERS: [number, number][] = [[80, 99], [150, 179], [250, 249], [Infinity, 349]];
export const d142Price = (licencies: number): number => D142_TIERS.find(([max]) => licencies <= max)![1];

export const PERKS: { key: Perk; title: string; sub: string }[] = [
  { key: 'season', title: 'Saison offerte', sub: 'jusqu’au 30 juin' },
  { key: 'months', title: 'Mois offerts', sub: '2, 3, 6…' },
  { key: 'fees', title: 'Frais offerts', sub: `mise en route, ${SETUP_FEE_TTC} €` },
  { key: 'discount', title: 'Remise', sub: 'en % la 1re saison' },
];

export type OfferStatus = 'draft' | 'scheduled' | 'live' | 'full' | 'ended' | 'cut';
export const STATUS_LABEL: Record<OfferStatus, string> = {
  draft: 'Brouillon', scheduled: 'Programmée', live: 'En ligne', full: 'Complète', ended: 'Terminée', cut: 'Coupée',
};

/** L'état d'une offre le jour `today` (AAAA-MM-JJ, Paris), comme la base (_launch_offer_open). */
export function offerStatus(o: Pick<LaunchOffer, 'active' | 'activated_at' | 'cut_at' | 'starts_on' | 'ends_on' | 'quota' | 'taken'>, today: string): OfferStatus {
  if (!o.active) return o.cut_at ? 'cut' : o.activated_at ? 'ended' : 'draft';
  if (o.ends_on && o.ends_on < today) return 'ended';
  if (o.taken >= o.quota) return 'full';
  if (o.starts_on && o.starts_on > today) return 'scheduled';
  return 'live';
}
/** Une offre visible sur arceau.app (en ligne, dans ses dates, des places). */
export const isOnPage = (o: Parameters<typeof offerStatus>[0], today: string) => offerStatus(o, today) === 'live';

/** Les places restantes, si la page les montre (sinon null : la date de fin seule). */
export function leftShown(o: Pick<LaunchOffer, 'quota' | 'taken' | 'show_left'>): number | null {
  const left = Math.max(0, o.quota - o.taken);
  return o.show_left || left < SHOW_LEFT_UNDER ? left : null;
}

const MONTHS = ['janvier', 'février', 'mars', 'avril', 'mai', 'juin', 'juillet', 'août', 'septembre', 'octobre', 'novembre', 'décembre'];
/** « 31 décembre » (l'année du jour) ou « 30 juin 2027 » (une autre année). */
export function frDay(iso: string, today?: string): string {
  const [y, m, d] = iso.slice(0, 10).split('-').map(Number);
  const sameYear = today ? Number(today.slice(0, 4)) === y : false;
  return `${d === 1 ? '1er' : d} ${MONTHS[m! - 1]}${sameYear ? '' : ` ${y}`}`;
}
/** Jours pleins jusqu'à la fin (la fin comprise), 0 le dernier jour. */
export function daysLeft(endsOn: string, today: string): number {
  return Math.round((Date.parse(`${endsOn}T12:00:00Z`) - Date.parse(`${today}T12:00:00Z`)) / 864e5);
}

/** L'avantage en clair (console, aperçu, page) : « L’app et le site gratuits jusqu’au 30 juin 2027 ». */
export function perkLine(o: Pick<LaunchOffer, 'perk' | 'perk_until' | 'perk_months' | 'perk_percent' | 'site_included'>, today?: string): string {
  const what = o.site_included ? 'L’app et le site gratuits' : 'L’app gratuite';
  if (o.perk === 'season') return `${what} jusqu’au ${o.perk_until ? frDay(o.perk_until, today) : '30 juin'}`;
  if (o.perk === 'months') return `${o.perk_months ?? 1} mois offert${(o.perk_months ?? 1) > 1 ? 's' : ''}${o.site_included ? ', l’app et le site' : ''}`;
  if (o.perk === 'fees') return `La mise en route offerte (${SETUP_FEE_TTC} € TTC)`;
  return `${o.perk_percent ?? 0} % de remise sur la première saison`;
}
/** Ce que l'avantage change à l'entrée du club (la carte « Pendant l’offre » de la console). */
export const replacesTrial = (perk: Perk) => perk === 'season' || perk === 'months';

/** La règle de fin, dite au visiteur : « Elle s’arrête au 50e club ou le 31 décembre à minuit, au premier des deux. » */
export function endRule(o: Pick<LaunchOffer, 'quota' | 'ends_on'>, today?: string): string {
  return `Elle s’arrête au ${o.quota}e club ou le ${o.ends_on ? frDay(o.ends_on, today) : '…'} à minuit, au premier des deux.`;
}

/** Contrôle de la saisie avant l'envoi (la base revérifie tout) : le premier problème, ou null. */
export function draftProblem(d: OfferDraft, entered = 0): string | null {
  const t = d.title.trim();
  if (t.length < 3 || t.length > 80) return 'Un titre de 3 à 80 caractères.';
  if (d.body.length > 400) return 'La phrase : 400 caractères au plus.';
  if (d.perk === 'season' && !d.perk_until) return 'Jusqu’à quelle date la saison est-elle offerte ?';
  if (d.perk === 'months' && !(d.perk_months && d.perk_months >= 1 && d.perk_months <= 12)) return 'De 1 à 12 mois offerts.';
  if (d.perk === 'discount' && !(d.perk_percent && d.perk_percent >= 5 && d.perk_percent <= 100)) return 'Une remise de 5 à 100 %.';
  if (!(Number.isInteger(d.quota) && d.quota >= 1 && d.quota <= 5000)) return 'Des places : de 1 à 5 000.';
  if (!(Number.isInteger(d.taken) && d.taken >= 0)) return 'Les places prises : un nombre.';
  if (d.taken < entered) return `Déjà ${entered} clubs entrés : pas moins de places prises.`;
  if (d.taken > d.quota) return 'Plus de places prises que de places.';
  if (d.starts_on && d.ends_on && d.starts_on > d.ends_on) return 'Le début vient avant la fin.';
  return null;
}
